import React from 'react';
import { contactHeading, tokenLabel } from './merge';
import PropertyPicker from './PropertyPicker';
import InvestorPicker from './InvestorPicker';

function formatLastEmailed(iso) {
    if (!iso) return '—';

    const date = new Date(iso);
    if (isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Where to fix a contact with no email: a lender's vendor page, a broker's or
// an investor's CRM record (the links the downloads page uses), else the
// record in iCLIMB. A broker's id carries a prefix that keeps it apart from
// an investor's.
function recordLink(row, noun) {
    if (noun === 'lender' || noun === 'vendor') return arc.path.base + 'vendor/' + row.id;
    if (row.kind === 'broker') {
        return row.crm_lead_id
            ? 'https://accounts.kore.co/crm/vendor/' + row.crm_lead_id
            : arc.path.base + 'agent/' + String(row.id).replace(/^broker-/, '');
    }
    return row.crm_lead_id
        ? 'https://accounts.kore.co/crm/lead/' + row.crm_lead_id
        : arc.path.base + 'investor/' + row.id;
}

/**
 * Who this send reaches, who it does not, and who still has a blank the letter
 * would print. The reassurance before an irreversible bulk action.
 *
 * Selecting and copying is blocked here at the client's request. It stops
 * someone dragging the list out with the mouse; it is not a lock, because the
 * data is already in the browser that is showing it.
 */
export default function Recipients({
    emailable, skipped, leftOut, needsAttention, missingFor, overrides, fillableTokens,
    activeIds, onPreview, onFill, onToggleExcluded,
    choosable, choiceFor, onChooseProperty, noun = 'owner',
    onAddInvestors, onRemoveAdded, refreshing,
}) {
    const [tab, setTab] = React.useState('send');
    const [search, setSearch] = React.useState('');
    const [adding, setAdding] = React.useState(false);
    // Off the Vendors or Agents list: nobody's property, so the columns name their firm instead.
    const isContactList = noun === 'vendor' || noun === 'agent';

    // Everyone already on this merge, whatever tab they sit under, so the
    // picker never offers them twice.
    const takenIds = emailable.concat(skipped, leftOut).map((row) => row.id);

    const addInvestors = (investors) => {
        setAdding(false);
        onAddInvestors(investors);
    };

    // The Left out tab goes with its last owner; staying on it would leave an
    // empty list under no highlighted tab.
    React.useEffect(() => {
        if (tab === 'out' && !leftOut.length) setTab('send');
    }, [tab, leftOut.length]);

    const source = tab === 'skip' ? skipped : tab === 'blank' ? needsAttention : tab === 'out' ? leftOut : emailable;

    const query = search.trim().toLowerCase();
    const rows = query
        ? source.filter((row) =>
            row.name.toLowerCase().indexOf(query) !== -1 ||
            (row.email || '').toLowerCase().indexOf(query) !== -1 ||
            row.addresses.join(' ').toLowerCase().indexOf(query) !== -1 ||
            (isContactList && (row.firm || '').toLowerCase().indexOf(query) !== -1))
        : source;

    const emptyText = query
        ? 'No ' + noun + 's match "' + search + '".'
        : tab === 'skip'
            ? 'Every ' + noun + ' in this list has an email on file.'
            : tab === 'blank'
                ? 'Nothing in this letter comes out blank for anyone.'
                : tab === 'out'
                    ? 'Nobody has been left out.'
                    : 'No ' + noun + 's with an email on file.';

    const stopCopy = (event) => event.preventDefault();

    const tabButton = (key, label) => (
        <button className={'mm-tab' + (tab === key ? ' mm-tab--on' : '')} onClick={() => setTab(key)}>
            {label}
        </button>
    );

    return (
        <div className="mm-card mm-recipients">
            <div className="mm-recipients__head">
                <div className="mm-panel__title" style={{ marginRight: 6 }}>Recipients</div>
                {tabButton('send', 'Will receive · ' + emailable.length)}
                {tabButton('skip', 'No email · ' + skipped.length)}
                {tabButton('blank', 'Blanks in the letter · ' + needsAttention.length)}
                {leftOut.length ? tabButton('out', 'Left out · ' + leftOut.length) : null}
                <input
                    className="mm-search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={'Search ' + noun + ', email or ' + (isContactList ? 'firm' : 'address')}
                />
                {onAddInvestors && !adding ? (
                    <button className="mm-btn mm-btn--sm" onClick={() => setAdding(true)} disabled={refreshing}>
                        {refreshing ? 'Adding…' : 'Add investors'}
                    </button>
                ) : null}
            </div>

            {onAddInvestors && adding ? (
                <div className="mm-recipients__add">
                    <InvestorPicker
                        takenIds={takenIds}
                        onAdd={addInvestors}
                        onCancel={() => setAdding(false)}
                    />
                </div>
            ) : null}

            {tab === 'skip' ? (
                <div className="mm-alert--warn">
                    Not emailed — these {noun}s have no email address on file.
                </div>
            ) : null}

            {tab === 'blank' ? (
                <div className="mm-alert--warn">
                    The letter would reach these {noun}s with a gap where a token should be.
                    Type the missing value in, or leave the {noun} out of this send.
                </div>
            ) : null}

            <div className="mm-rows__lock" onCopy={stopCopy} onCut={stopCopy} onContextMenu={stopCopy}>
                <div className="mm-row mm-row--header">
                    <div>{contactHeading(noun)}</div>
                    <div>Email</div>
                    <div>{noun === 'lender' ? 'Lender' : noun === 'contact' ? 'Type' : isContactList ? '' : 'Props'}</div>
                    <div>
                        {tab === 'blank'
                            ? 'Missing from the letter'
                            : isContactList ? 'Firm' : choosable ? 'The one this letter names' : 'Properties in this list'}
                    </div>
                    <div>Last emailed</div>
                    <div/>
                </div>

                <div className="mm-rows">
                    {rows.map((row) => (
                        <div
                            key={row.id}
                            className={'mm-row' + (tab === 'send' && activeIds.indexOf(row.id) !== -1 ? ' mm-row--active' : '')}
                        >
                            <div className="mm-row__name">
                                {row.name}
                                {row.added ? <span className="mm-pill mm-pill--info mm-row__added" title="Added by you, not an owner in this list">Added</span> : null}
                            </div>
                            {row.email
                                ? <div className="mm-row__muted">{row.email}</div>
                                : <div><span className="mm-pill mm-pill--warn">No email on file</span></div>}
                            <div className="mm-row__muted">{noun === 'lender'
                                ? row.lender
                                : noun === 'contact'
                                    ? (row.kind === 'broker' ? 'Broker' : 'Investor')
                                    : isContactList ? '' : row.property_count}</div>

                            {tab === 'blank' ? (
                                <div className="mm-row__fills">
                                    {missingFor(row).map((token) => (
                                        fillableTokens.indexOf(token) === -1
                                            ? <span className="mm-pill mm-pill--warn" key={token}>{tokenLabel(token)}</span>
                                            : <label className="mm-fill" key={token}>
                                                <span>{tokenLabel(token)}</span>
                                                <input
                                                    value={(overrides[row.id] || {})[token] || ''}
                                                    onChange={(event) => onFill(row.id, token, event.target.value)}
                                                    placeholder={'Type a ' + tokenLabel(token).toLowerCase()}
                                                />
                                            </label>
                                    ))}
                                </div>
                            ) : choosable && (row.properties || []).length > 1 ? (
                                // Picking one at a time in the preview means
                                // stepping through every owner, which is what
                                // the client was stuck doing.
                                <PropertyPicker
                                    properties={row.properties}
                                    value={choiceFor(row)}
                                    onChange={(propertyId) => onChooseProperty(row.id, propertyId)}
                                    ariaLabel={'Property this letter names for ' + row.name}
                                />
                            ) : isContactList ? (
                                <div className="mm-row__addrs">{row.firm || '—'}</div>
                            ) : (
                                <div className="mm-row__addrs">{row.addresses.length ? row.addresses.join('  ·  ') : '—'}</div>
                            )}

                            <div className="mm-row__muted">{formatLastEmailed(row.last_emailed_at)}</div>
                            <div className="mm-row__action">
                                {tab === 'out'
                                    ? <button className="mm-linkbtn" onClick={() => onToggleExcluded(row.id)}>Put back</button>
                                    : row.email
                                        ? <React.Fragment>
                                            <button className="mm-linkbtn" onClick={() => (tab === 'blank' ? onToggleExcluded(row.id) : onPreview(row.id))}>
                                                {tab === 'blank' ? 'Leave out' : 'Preview'}
                                            </button>
                                            {tab === 'send'
                                                // An added investor comes off the merge entirely;
                                                // a list owner moves to Left out, to be put back.
                                                ? <button
                                                    className="mm-linkbtn"
                                                    onClick={() => (row.added && onRemoveAdded ? onRemoveAdded(row.id) : onToggleExcluded(row.id))}
                                                    disabled={row.added && refreshing}
                                                >
                                                    Remove
                                                </button>
                                                : null}
                                        </React.Fragment>
                                        : <a href={recordLink(row, noun)}>Open {noun}</a>}
                            </div>
                        </div>
                    ))}
                    {rows.length === 0 ? <div className="mm-rows__empty">{emptyText}</div> : null}
                </div>
            </div>

            <div className="mm-recipients__foot">
                {rows.length}{query ? ' of ' + source.length : ''} {noun}s
            </div>
        </div>
    );
}
